<template>
  <div class="flex flex-col gap-6">
    <!-- Page Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-slate-800">申报书内容查重</h1>
        <p class="text-sm text-slate-500 mt-1">与历史申报书、已发表论文进行对比，分析申报书内容是否存在抄袭问题</p>
      </div>
      <div class="flex items-center gap-3">
        <span class="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-xs font-semibold">申报初审</span>
        <span class="px-3 py-1.5 bg-red-50 text-red-600 rounded-full text-xs font-semibold">发现问题</span>
      </div>
    </div>

    <!-- Main Content Card -->
    <div class="glass-card rounded-3xl p-6">
      <!-- Section 1: Check Items -->
      <div class="mb-8">
        <div class="flex items-center gap-2 mb-4">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">查重项配置</h2>
        </div>
        <p class="text-sm text-slate-500 mb-4 ml-10">选择需要查重的数据库，系统将对申报书进行多维度查重分析</p>

        <div class="ml-10 space-y-3">
          <label class="flex items-center gap-4 p-4 rounded-xl cursor-pointer border-2 transition-all duration-300" 
                 :class="selectedChecks.includes('internal') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'"
                 @click="toggleCheck('internal')">
            <div class="w-10 h-10 rounded-xl flex items-center justify-center transition-colors"
                 :class="selectedChecks.includes('internal') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"/>
              </svg>
            </div>
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <h3 class="text-sm font-semibold transition-colors" 
                    :class="selectedChecks.includes('internal') ? 'text-blue-700' : 'text-slate-700'">与历史申报书内容查重</h3>
                <div class="w-2 h-2 rounded-full transition-colors"
                     :class="selectedChecks.includes('internal') ? 'bg-emerald-500' : 'bg-slate-300'"></div>
              </div>
              <p class="text-xs text-slate-500 mt-1">与历史已立项的申报书进行对比，分析内容是否存在重复</p>
            </div>
          </label>

          <label class="flex items-center gap-4 p-4 rounded-xl cursor-pointer border-2 transition-all duration-300" 
                 :class="selectedChecks.includes('external') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'"
                 @click="toggleCheck('external')">
            <div class="w-10 h-10 rounded-xl flex items-center justify-center transition-colors"
                 :class="selectedChecks.includes('external') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
              </svg>
            </div>
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <h3 class="text-sm font-semibold transition-colors" 
                    :class="selectedChecks.includes('external') ? 'text-blue-700' : 'text-slate-700'">与外部论文内容查重</h3>
                <div class="w-2 h-2 rounded-full transition-colors"
                     :class="selectedChecks.includes('external') ? 'bg-emerald-500' : 'bg-slate-300'"></div>
              </div>
              <p class="text-xs text-slate-500 mt-1">与已发表论文进行对比，分析是否存在抄袭问题</p>
            </div>
          </label>
        </div>
      </div>

      <!-- Section 2: Input -->
      <div class="mb-8">
        <div class="flex items-center gap-2 mb-4">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">申报书信息输入</h2>
        </div>
        <p class="text-sm text-slate-500 mb-4 ml-10">输入申报书内容或上传文件，进行内容查重分析</p>

        <div class="ml-10">
          <div class="relative">
            <textarea
              v-model="declarationContent"
              class="premium-input w-full h-40 rounded-2xl p-5 text-sm leading-relaxed resize-none"
              placeholder="请粘贴申报书内容..."
            ></textarea>
            <div class="absolute bottom-4 right-4 text-xs text-slate-400">
              {{ declarationContent.length }} 字符
            </div>
          </div>

          <div class="mt-4 flex items-center gap-4">
            <button class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium text-slate-600 transition-colors">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
              </svg>
              上传文件
            </button>
            <span class="text-xs text-slate-400">支持 PDF、DOCX 格式</span>
          </div>
        </div>
      </div>

      <!-- Action Button -->
      <div class="flex justify-center">
        <button class="btn-action px-10 py-4 rounded-2xl text-white font-bold text-base flex items-center gap-3">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
          </svg>
          启动查重分析
        </button>
      </div>
    </div>

    <!-- Check Results -->
    <div class="glass-card rounded-3xl p-6">
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-2">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">查重结果</h2>
        </div>
        <button class="btn-export px-4 py-2 rounded-xl text-white text-sm font-medium flex items-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
          </svg>
          导出报告
        </button>
      </div>

      <div class="space-y-4">
        <!-- Internal Check Result - Has Issues -->
        <div class="p-4 bg-red-50 border border-red-200 rounded-xl">
          <div class="flex items-start gap-3">
            <div class="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center flex-shrink-0">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
            <div class="flex-1">
              <h4 class="font-semibold text-red-800 text-sm">与历史申报书内容查重：发现高危问题</h4>
              <p class="text-xs text-red-600 mt-1">本项目存在与项目 S20YBB557 疑似重复风险、与项目 S20ZDG381 疑似局部交叉重复风险</p>
              
              <!-- Detailed Results -->
              <div class="mt-4 space-y-3">
                <div class="p-3 bg-white rounded-lg border border-red-100">
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-sm font-medium text-slate-700">与项目 S20YBB557 疑似重复风险</span>
                    <span class="text-lg font-bold text-red-500">84.61%</span>
                  </div>
                  <p class="text-xs text-slate-600">两份申报书在研究方法和工艺技术路线及项目创新点模块高度相似</p>
                </div>
                <div class="p-3 bg-white rounded-lg border border-red-100">
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-sm font-medium text-slate-700">与项目 S20ZDG381 疑似局部交叉重复风险</span>
                    <span class="text-lg font-bold text-amber-500">63.86%</span>
                  </div>
                  <p class="text-xs text-slate-600">两份申报书在成果应用展望与研究目的意义模块中高度雷同</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- External Check Result - No Issues -->
        <div class="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
            </div>
            <div class="flex-1">
              <h4 class="font-semibold text-emerald-800 text-sm">与外部论文内容查重：无异常</h4>
              <p class="text-xs text-emerald-600 mt-1">外部数据库比对完成，未发现与已发表论文存在重复或抄袭问题</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DeclareDuplicateCheck',
  data() {
    return {
      selectedChecks: ['internal', 'external'],
      declarationContent: ''
    }
  },
  methods: {
    toggleCheck(check) {
      const index = this.selectedChecks.indexOf(check)
      if (index > -1) {
        this.selectedChecks.splice(index, 1)
      } else {
        this.selectedChecks.push(check)
      }
    }
  }
}
</script>
